import { alertApi, type AlertApi } from '../api/AlertApi'
import type { AlertSubscriptionDto } from '../models/alert.dto'
import type { AlertDraft, AlertRule } from '../models/alert.types'
import type { ChannelAvailability } from '../models/channel.types'
import type { AlertHistoryRow } from '../models/history.types'
import type { AlertChannelAvailabilitySource } from '../models/notification-bridge'
import type { AlertLevelMetadata, AlertMetricCatalog } from '../models/reports-bridge'

import { alertChannelAvailability, mergeAlertChannelAvailability } from './alertChannelMapper'
import { alertDraftToDto, alertDraftToUpdateDto } from './alertMapper'
import { alertHistory, alertRules, type AlertReadMapperDependencies } from './alertReadMapper'
import { alertSaveFieldError, type AlertSaveFieldError } from './alertSaveError'
import { AlertValidationError, validateAlertDraft } from './alertValidator'

import { ALERT_HISTORY_OCCURRENCE_LIMIT } from '@/shared/config'
import { ApiError } from '@/shared/http'

type AlertSaveApi = Pick<AlertApi, 'create' | 'update'>
type AlertReadApi = Pick<AlertApi, 'list' | 'getTriggered' | 'setActive' | 'remove'>

export type AlertWorkspaceId = number | string

export type AlertChannelLoader = (
  workspaceId: AlertWorkspaceId,
) => Promise<readonly AlertChannelAvailabilitySource[]>

export interface AlertLoadContext {
  notificationTypeId: number
  awaitCatalog: () => Promise<void>
  metricCatalog: () => AlertMetricCatalog
  levelMetadata: () => AlertLevelMetadata
  formatDate: (value: string) => string
  loadChannels: AlertChannelLoader
}

export type AlertUnavailableSliceName = 'subscriptions' | 'triggered' | 'channels'

export interface AlertUnavailableSlice {
  workspaceId: AlertWorkspaceId
  slice: AlertUnavailableSliceName
  error: ApiError
}

export interface AlertProjection {
  rules: AlertRule[]
  history: AlertHistoryRow[]
}

export interface AlertBundle extends AlertProjection {
  channels: ChannelAvailability[]
  unavailableSlices: AlertUnavailableSlice[]
  project?: () => AlertProjection
}

export interface AlertSaveContext {
  workspaceId: number | string
  notificationTypeId: number
  idempotencyKey: string
  channelAvailability: readonly ChannelAvailability[]
}

export interface AlertSaveResult {
  ok: boolean
  subscription?: AlertSubscriptionDto
  error?: unknown
  fieldError: AlertSaveFieldError | null
}

interface AlertSaveOperation {
  run: () => Promise<AlertSubscriptionDto>
}

interface WorkspaceRead {
  workspaceId: AlertWorkspaceId
  subscriptions: PromiseSettledResult<AlertSubscriptionDto[]>
  triggered: PromiseSettledResult<Awaited<ReturnType<AlertReadApi['getTriggered']>>>
  channels: PromiseSettledResult<readonly AlertChannelAvailabilitySource[]>
}

/** Compatibility save boundary: one request for the whole draft — every metric shares a single
 * `content_details` body (ORP-XXXXX; see `alertMapper.ts`'s `editableContentDetails`). */
export class AlertService {
  private readonly readApi: AlertReadApi

  constructor(
    private readonly saveApi: AlertSaveApi = alertApi,
    readApi: AlertReadApi = alertApi,
  ) {
    this.readApi = readApi
  }

  async loadAll(
    workspaceIds: readonly AlertWorkspaceId[],
    context: AlertLoadContext,
  ): Promise<AlertBundle> {
    const ids = [...new Map(workspaceIds.map((id) => [String(id), id])).values()]
    if (!ids.length) return { rules: [], history: [], channels: [], unavailableSlices: [] }

    const [workspaces] = await Promise.all([
      Promise.all(ids.map((workspaceId) => this.readWorkspace(workspaceId, context))),
      context.awaitCatalog(),
    ])
    const unavailableSlices = workspaces.flatMap((workspace) => this.unavailable(workspace))
    const listSuccesses = workspaces.filter(
      (workspace) => workspace.subscriptions.status === 'fulfilled',
    )
    if (!listSuccesses.length) {
      const failure = unavailableSlices.find((item) => item.slice === 'subscriptions')
      throw failure?.error ?? ApiError.fromStatus(null, 'Alert subscriptions are unavailable')
    }

    const channelSlices = workspaces.flatMap((workspace) =>
      workspace.channels.status === 'fulfilled'
        ? [alertChannelAvailability(workspace.channels.value)]
        : [],
    )
    const project = (): AlertProjection => this.project(workspaces, context)
    const { rules, history } = project()
    return {
      rules,
      history,
      channels: mergeAlertChannelAvailability(channelSlices),
      unavailableSlices,
      project,
    }
  }

  private project(
    workspaces: readonly WorkspaceRead[],
    context: AlertLoadContext,
  ): AlertProjection {
    const catalog = context.metricCatalog()
    const dependencies: AlertReadMapperDependencies = {
      catalog,
      levels: context.levelMetadata(),
      formatDate: context.formatDate,
      metricLabel: (field) => catalog.metricLabel(field),
    }
    const rules: AlertRule[] = []
    const history: AlertHistoryRow[] = []

    for (const workspace of workspaces) {
      const subscriptions =
        workspace.subscriptions.status === 'fulfilled' ? workspace.subscriptions.value : []
      const triggered = workspace.triggered.status === 'fulfilled' ? workspace.triggered.value : []
      if (workspace.subscriptions.status === 'fulfilled') {
        rules.push(...alertRules(subscriptions, triggered, dependencies, [workspace.workspaceId]))
      }
      if (workspace.triggered.status === 'fulfilled') {
        history.push(
          ...alertHistory(subscriptions, triggered, dependencies, [workspace.workspaceId]),
        )
      }
    }

    history.sort(
      (left, right) => this.timeValue(right.triggeredAt) - this.timeValue(left.triggeredAt),
    )
    return { rules, history }
  }

  setActive(id: number, active: boolean): Promise<void> {
    return this.readApi.setActive(id, active)
  }

  remove(id: number): Promise<void> {
    return this.readApi.remove(id)
  }

  async save(draft: AlertDraft, context: AlertSaveContext): Promise<AlertSaveResult> {
    const validation = validateAlertDraft(draft, {
      channelAvailability: context.channelAvailability,
    })
    if (!validation.ok) throw new AlertValidationError(validation)

    try {
      const subscription = await this.operation(draft, context).run()
      return { ok: true, subscription, fieldError: null }
    } catch (error) {
      return { ok: false, error, fieldError: alertSaveFieldError(error) }
    }
  }

  private operation(draft: AlertDraft, context: AlertSaveContext): AlertSaveOperation {
    const subscriptionId = draft.id
    if (draft.readOnly) {
      if (subscriptionId === undefined) {
        throw new Error('Read-only alert is missing its subscription id')
      }
      return { run: () => this.saveApi.update(subscriptionId, alertDraftToUpdateDto(draft)) }
    }
    if (subscriptionId !== undefined) {
      return { run: () => this.saveApi.update(subscriptionId, alertDraftToUpdateDto(draft)) }
    }
    return {
      run: () =>
        this.saveApi.create(
          context.workspaceId,
          context.notificationTypeId,
          alertDraftToDto(draft),
          context.idempotencyKey,
        ),
    }
  }

  private async readWorkspace(
    workspaceId: AlertWorkspaceId,
    context: AlertLoadContext,
  ): Promise<WorkspaceRead> {
    const [subscriptions, triggered, channels] = await Promise.allSettled([
      this.readApi.list(workspaceId, context.notificationTypeId),
      this.readApi.getTriggered(workspaceId, ALERT_HISTORY_OCCURRENCE_LIMIT),
      context.loadChannels(workspaceId),
    ])
    return { workspaceId, subscriptions, triggered, channels }
  }

  private unavailable(workspace: WorkspaceRead): AlertUnavailableSlice[] {
    const unavailable: AlertUnavailableSlice[] = []
    if (workspace.subscriptions.status === 'rejected') {
      unavailable.push({
        workspaceId: workspace.workspaceId,
        slice: 'subscriptions',
        error: this.toApiError(workspace.subscriptions.reason),
      })
    }
    if (workspace.triggered.status === 'rejected') {
      unavailable.push({
        workspaceId: workspace.workspaceId,
        slice: 'triggered',
        error: this.toApiError(workspace.triggered.reason),
      })
    }
    if (workspace.channels.status === 'rejected') {
      unavailable.push({
        workspaceId: workspace.workspaceId,
        slice: 'channels',
        error: this.toApiError(workspace.channels.reason),
      })
    }
    return unavailable
  }

  private toApiError(cause: unknown): ApiError {
    return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }

  private timeValue(value: string | null): number {
    if (!value) return 0
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
}

export const alertService = new AlertService()
